/**
 * Track geometry: the numbered positions ("slots") around the grid.
 *
 * Picture the grid with a ring of squares around it, one square deep. Each
 * square in that ring is a slot. Slot 0 is the bottom-left corner, and the
 * numbers go anticlockwise as you look at the screen: right along the bottom,
 * up the right side, left along the top, then down the left side back to the
 * start. (Anticlockwise on screen, because screen y grows downward.)
 *
 * Corner slots don't face anything. Every other slot faces exactly one row or
 * column of the grid, and that's the line a runner fires along when it passes.
 *
 * Everything here is plain arithmetic on numbers, with no drawing, which is
 * why the rules (state.ts) can use it and so can the screen layout
 * (layout.ts).
 */

/**
 * The four sides of the grid, in the order the track visits them. `as const`
 * tells TypeScript these exact strings never change, which lets it derive the
 * `Side` type below from this list.
 */
export const SIDES = ["bottom", "right", "top", "left"] as const;
/**
 * One of "bottom" | "right" | "top" | "left", derived from `SIDES` so the list
 * and the type can never disagree.
 */
export type Side = (typeof SIDES)[number];

/**
 * What's at a slot: a corner (faces nothing), or a "face" slot looking at one
 * row or column of the grid. For a face, `side` says which side of the grid
 * it's on, and `line` says which row or column it looks along.
 */
export type Slot =
	| { kind: "corner" }
	| { kind: "face"; side: Side; line: number };

/**
 * How many slots there are around a `width` x `height` grid: one for each
 * cell along each edge, plus the four corners.
 */
export function trackLength(width: number, height: number): number {
	return 2 * width + 2 * height + 4;
}

/**
 * What the slot at `index` is (0 up to `trackLength - 1`). Works by walking
 * the four sides in order, subtracting each side's length from `index` until
 * it lands inside one.
 */
export function slotAt(index: number, width: number, height: number): Slot {
	let i = index;
	if (i === 0) return { kind: "corner" };
	i -= 1;
	if (i < width) return { kind: "face", side: "bottom", line: i };
	i -= width;
	if (i === 0) return { kind: "corner" };
	i -= 1;
	if (i < height) return { kind: "face", side: "right", line: height - 1 - i };
	i -= height;
	if (i === 0) return { kind: "corner" };
	i -= 1;
	if (i < width) return { kind: "face", side: "top", line: width - 1 - i };
	i -= width;
	if (i === 0) return { kind: "corner" };
	i -= 1;
	return { kind: "face", side: "left", line: i };
}

/**
 * Where a slot is, in grid coordinates (the grid itself covers 0 to
 * width - 1 across and 0 to height - 1 down). Track slots sit just outside the
 * grid, so they have coordinates like -1 or `width`.
 */
export function slotCoords(
	index: number,
	width: number,
	height: number,
): { x: number; y: number } {
	let i = index;
	if (i <= width) return { x: i - 1, y: height };
	i -= width + 1;
	if (i <= height) return { x: width, y: height - i };
	i -= height + 1;
	if (i <= width) return { x: width - i, y: -1 };
	i -= width + 1;
	return { x: -1, y: i - 1 };
}

/**
 * Where a runner is, in grid coordinates, for a decimal track position. A
 * position of 3.25 is a quarter of the way from slot 3 to slot 4, so the result
 * is found by blending ("interpolating") between those two slots' coordinates.
 */
export function trackPoint(
	position: number,
	width: number,
	height: number,
): { x: number; y: number } {
	const length = trackLength(width, height);
	const base = Math.floor(position);
	const t = position - base;
	const a = slotCoords(((base % length) + length) % length, width, height);
	const b = slotCoords(
		(((base + 1) % length) + length) % length,
		width,
		height,
	);
	return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
